import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import axios from "../_API/axiosConfig";

const WINDOW_TARGET = "_blank";
const WINDOW_FEATURES = "noopener,noreferrer";
const EXTENSION_REGEX = /\.[a-z0-9]{2,5}$/i;
const MIME_EXTENSION_MAP = {
  "application/pdf": ".pdf",
  "application/zip": ".zip",
  "application/x-zip-compressed": ".zip",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
  "application/vnd.ms-excel": ".xls",
  "text/csv": ".csv",
  "application/json": ".json",
};

const BLOCKED_CONTENT_TYPES = [
  "application/json",
  "text/html",
  "application/problem+json",
  "application/xml",
  "text/xml",
];

const parseErrorPayload = async (response) => {
  const ct = (response.headers.get("content-type") || "").toLowerCase();
  try {
    if (ct.includes("application/json") || ct.includes("+json")) {
      const data = await response.json();
      const msg =
        data?.detail ||
        data?.error ||
        data?.message ||
        data?.non_field_errors?.[0] ||
        "Error al descargar archivo";
      return String(msg);
    }
    const text = await response.text();
    const clean = (text || "").replace(/\s+/g, " ").trim();
    return clean.slice(0, 240) || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
};

const normalizeDownloadUrl = (rawUrl = "") => {
  if (!rawUrl || typeof rawUrl !== "string") return rawUrl;
  if (/^https?:\/\//i.test(rawUrl) || rawUrl.startsWith("blob:")) return rawUrl;
  if (rawUrl.startsWith("//")) {
    const protocol =
      typeof window !== "undefined" && window.location?.protocol
        ? window.location.protocol
        : "https:";
    return `${protocol}${rawUrl}`;
  }

  const baseRaw = String(axios.defaults.baseURL || process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const base =
    rawUrl.startsWith("/api/") && /\/api$/i.test(baseRaw)
      ? baseRaw.replace(/\/api$/i, "")
      : baseRaw;

  if (rawUrl.startsWith("/")) {
    return base ? `${base}${rawUrl}` : rawUrl;
  }
  return base ? `${base}/${rawUrl}` : rawUrl;
};

const isDownloadableContentType = (contentType = "", contentDisposition = "", guessedName = "") => {
  const ct = (contentType || "").split(";")[0].trim().toLowerCase();
  const cd = (contentDisposition || "").toLowerCase();
  if (cd.includes("attachment")) return true;
  if (!ct) {
    return EXTENSION_REGEX.test(guessedName || "");
  }
  if (BLOCKED_CONTENT_TYPES.some((x) => ct.includes(x))) return false;
  if (ct === "text/plain") return false;
  if (
    ct.startsWith("application/") ||
    ct.startsWith("image/") ||
    ct.startsWith("audio/") ||
    ct.startsWith("video/") ||
    ct === "text/csv"
  ) {
    return true;
  }
  return false;
};

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = (err) => reject(err);
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result === "string") {
        const base64 = result.split(",")[1] || "";
        resolve(base64);
      } else {
        reject(new Error("blobToBase64: unexpected reader result"));
      }
    };
    reader.readAsDataURL(blob);
  });

const openWithWindow = (url) => {
  if (typeof window === "undefined") return null;
  const openFn = window?.["open"];
  if (typeof openFn !== "function") return null;
  try {
    return openFn.call(window, url, WINDOW_TARGET, WINDOW_FEATURES);
  } catch (err) {
    console.warn("[mobileBridge] fallback open failed", err);
    return null;
  }
};

const openBlobInWindow = (blob) => {
  if (typeof window === "undefined") return;
  const objectUrl = URL.createObjectURL(blob);
  openWithWindow(objectUrl);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
};

const guessFilename = (url, provided) => {
  if (provided) return provided;
  try {
    const parsed = new URL(url, typeof window !== "undefined" ? window.location.href : undefined);
    const pathname = parsed.pathname || "";
    const name = pathname.split("/").pop();
    if (name) return name;
  } catch {
    // ignore parsing errors
  }
  return `file-${Date.now()}`;
};

const inferExtensionFromUrl = (url = "") => {
  try {
    const parsed = new URL(url, typeof window !== "undefined" ? window.location.href : undefined);
    const pathname = parsed.pathname || "";
    const name = pathname.split("/").pop() || "";
    if (EXTENSION_REGEX.test(name)) {
      const ext = name.match(EXTENSION_REGEX);
      return ext ? ext[0] : "";
    }
  } catch {
    // ignore parse errors
  }
  return "";
};

const inferExtensionFromMime = (mime = "") => {
  const clean = mime.split(";")[0]?.trim().toLowerCase();
  if (!clean) return "";
  if (MIME_EXTENSION_MAP[clean]) return MIME_EXTENSION_MAP[clean];
  if (clean.startsWith("image/")) {
    const sub = clean.split("/")[1] || "jpg";
    return `.${sub}`;
  }
  if (clean.startsWith("text/")) {
    const sub = clean.split("/")[1] || "txt";
    return `.${sub}`;
  }
  return "";
};

const ensureExtension = (name, contentType, url) => {
  if (!name) return guessFilename(url);
  if (EXTENSION_REGEX.test(name)) {
    return name;
  }
  const fromMime = inferExtensionFromMime(contentType);
  const fromUrl = inferExtensionFromUrl(url);
  const ext = fromMime || fromUrl;
  if (ext) return `${name}${ext}`;
  return name;
};

export const isNative = () => {
  try {
    return (
      typeof Capacitor !== "undefined" &&
      typeof Capacitor.isNativePlatform === "function" &&
      Capacitor.isNativePlatform()
    );
  } catch {
    return false;
  }
};

export const openUrl = (url) => {
  if (!url) return;
  if (isNative()) {
    return Browser.open({ url }).catch((err) => {
      console.warn("[mobileBridge] Browser.open failed, fallback to window target", err);
      openWithWindow(url);
    });
  }
  openWithWindow(url);
};

export const downloadFile = async ({ url, filename, mimeType, headers } = {}) => {
  if (!url) return null;
  const finalUrl = normalizeDownloadUrl(url);
  const normalizedHeaders = headers || undefined;
  const hasAuthHeader = Boolean(
    normalizedHeaders?.Authorization || normalizedHeaders?.authorization
  );

  if (!isNative()) {
    if (!normalizedHeaders) {
      openUrl(finalUrl);
      return null;
    }
    try {
      const response = await fetch(finalUrl, { headers: normalizedHeaders });
      const contentType = response.headers.get("content-type") || "";
      const contentDisposition = response.headers.get("content-disposition") || "";
      const guessedName = guessFilename(finalUrl, filename);
      console.info("[downloadFile:web] status=%s ct=%s cd=%s", response.status, contentType, contentDisposition);
      if (!response.ok) {
        const detail = await parseErrorPayload(response);
        throw new Error(`No se pudo descargar (${response.status}): ${detail}`);
      }
      if (!isDownloadableContentType(contentType, contentDisposition, guessedName)) {
        const detail = await parseErrorPayload(response);
        throw new Error(`Respuesta no descargable (${contentType || "sin content-type"}): ${detail}`);
      }
      const blob = await response.blob();
      console.info(
        "[downloadFile:web] blob-size=%s mime=%s",
        blob?.size ?? 0,
        blob?.type || contentType || "unknown"
      );
      if (!blob || blob.size <= 0) {
        throw new Error("El archivo llegó vacío.");
      }
      openBlobInWindow(blob);
      return null;
    } catch (error) {
      console.warn("[mobileBridge] downloadFile(web) failed", error);
      if (hasAuthHeader) {
        throw error instanceof Error ? error : new Error("No se pudo descargar el archivo");
      }
      openUrl(finalUrl);
      return null;
    }
  }

  try {
    const response = await fetch(finalUrl, {
      headers: normalizedHeaders,
    });
    const responseContentType = response.headers.get("content-type") || "";
    const contentDisposition = response.headers.get("content-disposition") || "";
    const guessedName = guessFilename(finalUrl, filename);
    console.info("[downloadFile:native] status=%s ct=%s cd=%s", response.status, responseContentType, contentDisposition);
    if (!response.ok) {
      const detail = await parseErrorPayload(response);
      throw new Error(`No se pudo descargar (${response.status}): ${detail}`);
    }
    if (!isDownloadableContentType(responseContentType, contentDisposition, guessedName)) {
      const detail = await parseErrorPayload(response);
      throw new Error(`Respuesta no descargable (${responseContentType || "sin content-type"}): ${detail}`);
    }
    const blob = await response.blob();
    console.info(
      "[downloadFile:native] blob-size=%s mime=%s",
      blob?.size ?? 0,
      blob?.type || responseContentType || "unknown"
    );
    if (!blob || blob.size <= 0) {
      throw new Error("El archivo llegó vacío.");
    }
    const base64 = await blobToBase64(blob);
    const resolvedContentType = mimeType || blob.type || responseContentType || "application/octet-stream";
    const finalName = ensureExtension(guessedName, resolvedContentType, finalUrl);
    const saved = await Filesystem.writeFile({
      path: finalName,
      data: base64,
      directory: Directory.Documents,
      recursive: true,
    });
    await Share.share({
      title: finalName,
      text: finalName,
      url: saved.uri,
      dialogTitle: finalName,
    });
    return { uri: saved.uri, mimeType: resolvedContentType };
  } catch (error) {
    console.warn("[mobileBridge] downloadFile(native) failed", error);
    if (hasAuthHeader) {
      throw error instanceof Error ? error : new Error("No se pudo descargar el archivo");
    }
    openUrl(finalUrl);
    return null;
  }
};

export default {
  isNative,
  openUrl,
  downloadFile,
};
